<template>
  <div class="card card-custom card-shadowless border insight-connection-panel mb-5">
    <div class="card-header border-0 py-4">
      <div class="card-title align-items-center">
        <span class="card-label font-weight-bolder text-dark">
          <i class="fa fa-plug mr-2 text-primary"></i>
          INSIGHT Connection
        </span>
      </div>
      <div class="card-toolbar">
        <b-badge v-if="connection && connection.renewal" :variant="renewalBadgeVariant" class="font-weight-bold p-2">
          {{ renewalBadgeLabel }}
        </b-badge>
      </div>
    </div>

    <div class="card-body pt-2 pb-4">
      <!-- Loading -->
      <div v-if="loading" class="text-muted font-size-sm">
        <i class="fa fa-spinner fa-spin mr-1"></i> Checking INSIGHT connection…
      </div>

      <!-- Handled degrade (row not found / not an operational row) - guidance verbatim -->
      <b-alert v-else-if="loadError" variant="warning" show class="mb-0">
        <div class="text-dark">{{ loadError }}</div>
      </b-alert>

      <div v-else-if="connection && connection.found">
        <!-- Three lights. Codex-H2: each light's honesty comes from family.verified, not the light
             colour alone - a green anchor-only or shallow family reads differently from a green
             live-verified one; "verified in INSIGHT" wording renders ONLY for verified === 'live'. -->
        <div class="d-flex flex-wrap mb-3">
          <div
            v-for="light in lights"
            :key="light.key"
            class="connection-light mr-6 mb-2"
            v-b-tooltip.hover
            :title="light.detail"
          >
            <span class="light-dot mr-2" :class="dotClass(light.value.light)"></span>
            <span class="font-weight-bold text-dark">{{ light.label }}</span>
            <span class="text-muted font-size-sm ml-1" v-if="light.value.id">#{{ light.value.id }}</span>
            <span class="text-muted font-size-sm ml-1" v-if="verifiedLabel(light.value.verified)">
              - {{ verifiedLabel(light.value.verified) }}
            </span>
          </div>
        </div>

        <!-- Deep-verify mode note (Codex-H2: renamed deepVerified -> deepMode; this is the MODE, not a
             blanket "everything is verified" claim - each light's own verified label carries the truth). -->
        <div class="text-muted font-size-sm mb-3">
          <span v-if="connection.deepMode">
            <i class="fa fa-shield-alt mr-1"></i> INSIGHT deep verify is on - see each light above for its verified state.
          </span>
          <span v-else>
            <i class="fa fa-info-circle mr-1"></i> Status from Zoho record ids (INSIGHT deep verify off).
          </span>
        </div>

        <!-- Exception / blocked banner (Exception dominates) -->
        <b-alert v-if="connection.blocked" variant="warning" show>
          <div class="text-dark">
            <strong>This row is in Exception.</strong>
            Resolve the exception before connecting to INSIGHT.
          </div>
        </b-alert>

        <!-- Connect result - guidance rendered verbatim. Hide stale failure when already connected. -->
        <b-alert v-if="showConnectResult" :variant="connectAlertVariant" show>
          <div class="text-dark">
            <!-- P7-A-FIX2 (backstop Low): the pending shape is progress, not failure - never
                 failure-toned wording for it. -->
            <strong>{{ connectResultHeading }}</strong>
            <span v-if="connectResult.error"> {{ connectResult.error }}</span>
            <span v-else-if="connectResult.detail"> {{ connectResult.detail }}</span>
            <ul v-if="connectResult.steps && connectResult.steps.length" class="mb-0 mt-2">
              <li v-for="(s, index) in connectResult.steps" :key="index">
                <strong>{{ stepLabel(s.step) }}:</strong>
                <span v-if="s.error">{{ s.error }}</span>
                <span v-else>{{ s.detail || (s.done ? "done" : "attempted") }}</span>
              </li>
            </ul>
          </div>
        </b-alert>

        <!-- Connect button -->
        <div class="d-flex align-items-center">
          <button
            class="btn btn-primary font-weight-bold text-uppercase"
            :disabled="!canConnect"
            @click.prevent="promptConnect"
          >
            <i class="fa fa-plug"></i>
            {{ connecting ? "Connecting…" : "Connect to INSIGHT" }}
          </button>
          <span v-if="connectResult && (connectResult.takeUpComplete || connectResult.pending)" class="text-muted font-size-sm ml-3">
            Refresh the page. Do not click Connect again.
          </span>
          <span v-else-if="allGreen && !connection.renewal.takeUpNeeded" class="text-muted font-size-sm ml-3">
            Client, broker and policy are all connected.
          </span>
          <span v-else-if="connection.blocked" class="text-muted font-size-sm ml-3">
            Blocked until the exception is resolved.
          </span>
          <span v-else-if="hasUnrepairableAmber" class="text-muted font-size-sm ml-3">
            Connect cannot repair this - a family could not be verified in INSIGHT. Investigate in INSIGHT.
          </span>
        </div>
      </div>

      <!-- Confirm dialog - the human gate for the composite write action -->
      <b-modal
        ref="connectModal" title="Connect to INSIGHT" size="md"
        no-close-on-backdrop ok-title="Connect" cancel-title="Cancel"
        @ok="doConnect"
      >
        <b-card-text>
          This creates and links the missing INSIGHT records for this transaction in order:
          the <strong>client</strong>, the <strong>producing broker</strong>, then the
          <strong>policy take-up</strong>. It stops at the first step that needs attention and
          never posts. Continue?
        </b-card-text>
      </b-modal>
    </div>
  </div>
</template>

<script>
import {
  getInsightConnectionStatus,
  connectToInsight
} from "@/services/workbench-service";

export default {
  name: "InsightConnectionPanel",

  props: {
    // The K_IbaisTransactions ledger-row id (the `ledger-` route id with the prefix stripped).
    ledgerRowId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      loading: false,
      loadError: null,
      connection: null,
      connecting: false,
      connectResult: null
    };
  },

  mounted() {
    this.loadStatus();
  },

  computed: {
    lights: function() {
      if (!this.connection) {
        return [];
      }
      return [
        { key: "client", label: "Client", value: this.connection.client, detail: this.connection.client.detail },
        { key: "broker", label: "Producing broker", value: this.connection.broker, detail: this.connection.broker.detail },
        { key: "policy", label: "Policy", value: this.connection.policy, detail: this.connection.policy.detail }
      ];
    },

    allGreen: function() {
      if (!this.connection) {
        return false;
      }
      return (
        this.connection.client.light === "green" &&
        this.connection.broker.light === "green" &&
        this.connection.policy.light === "green"
      );
    },

    // Codex-M1 (P7-A-FIX): driven by the SERVER's nextAction, not "any non-green light" - an amber
    // family (verified:'failed') with no connectable nextAction (nothing Connect can do about it) must
    // NOT enable the button; mapNextAction already resolves to 'prepare-transaction' whenever the
    // policy/priced flags are failing, so a renewal take-up still due is already covered here.
    canConnect: function() {
      if (!this.connection || this.connection.blocked || this.connecting) {
        return false;
      }
      if (
        this.connectResult
        && (this.connectResult.pending || this.connectResult.takeUpComplete)
      ) {
        return false;
      }
      const key = this.connection.nextAction && this.connection.nextAction.key;
      return ["create-insight-client", "link-subagent", "prepare-transaction"].indexOf(key) !== -1;
    },

    // Codex-M1: an amber family Connect cannot repair (its nextAction key isn't one of the three
    // connectable actions) - guidance to investigate in INSIGHT rather than a dead disabled button.
    hasUnrepairableAmber: function() {
      if (!this.connection || this.connection.blocked) {
        return false;
      }
      if (
        this.connectResult
        && (this.connectResult.pending || this.connectResult.takeUpComplete)
      ) {
        return false;
      }
      const anyAmber = [this.connection.client, this.connection.broker, this.connection.policy].some(
        f => f.light === "amber"
      );
      return anyAmber && !this.canConnect;
    },

    renewalBadgeLabel: function() {
      const r = this.connection && this.connection.renewal;
      if (!r) {
        return "";
      }
      const base = r.isRenewal ? "Renewal" : "New Business";
      return r.takeUpNeeded ? `${base} · take-up due` : base;
    },

    renewalBadgeVariant: function() {
      const r = this.connection && this.connection.renewal;
      if (!r) {
        return "light";
      }
      return r.takeUpNeeded ? "light-primary" : "light-success";
    },

    alreadyConnected: function() {
      return !!(
        this.connection
        && this.allGreen
        && this.connection.renewal
        && !this.connection.renewal.takeUpNeeded
        && !this.canConnect
      );
    },

    showConnectResult: function() {
      if (!this.connectResult) {
        return false;
      }
      // Do not keep a failure-toned banner when lights already show fully connected.
      if (this.alreadyConnected && !this.connectResult.done && !this.connectResult.pending) {
        return false;
      }
      return true;
    },

    connectResultHeading: function() {
      if (!this.connectResult) {
        return "";
      }
      if (this.connectResult.takeUpComplete) {
        return "Take-up complete.";
      }
      if (this.connectResult.done) {
        return "Connected.";
      }
      if (this.connectResult.pending) {
        return "Finished — refresh, do not click Connect again.";
      }
      return "Not fully connected.";
    },

    connectAlertVariant: function() {
      if (!this.connectResult) {
        return "light";
      }
      if (this.connectResult.done || this.connectResult.takeUpComplete) {
        return "success";
      }
      // Pending = the step ran; Zoho has not caught up. Info, not a failure.
      return this.connectResult.pending ? "info" : "warning";
    }
  },

  methods: {
    apiErrorDetail: function(err, fallback) {
      const data = err && err.response && err.response.data;
      const detail = data && (data.error || data.message || data.result);
      return detail ? String(detail) : fallback;
    },

    loadStatus: function() {
      this.loading = true;
      this.loadError = null;
      getInsightConnectionStatus(this.ledgerRowId)
        .then(model => {
          if (model && model.error) {
            this.loadError = model.error;
            this.connection = null;
          } else {
            this.connection = model;
            // Drop stale Connect failure once status shows nothing left to connect.
            if (
              this.connectResult
              && !this.connectResult.done
              && !this.connectResult.pending
              && model
              && model.client
              && model.client.light === "green"
              && model.broker
              && model.broker.light === "green"
              && model.policy
              && model.policy.light === "green"
              && model.renewal
              && !model.renewal.takeUpNeeded
            ) {
              const key = model.nextAction && model.nextAction.key;
              if (["create-insight-client", "link-subagent", "prepare-transaction"].indexOf(key) === -1) {
                this.connectResult = null;
              }
            }
          }
          this.loading = false;
        })
        .catch((err) => {
          this.loadError = this.apiErrorDetail(
            err,
            "Unable to check the INSIGHT connection status for this row."
          );
          this.loading = false;
        });
    },

    promptConnect: function() {
      if (!this.canConnect) {
        return;
      }
      this.$refs.connectModal.show();
    },

    doConnect: function() {
      this.connecting = true;
      this.connectResult = null;
      connectToInsight(this.ledgerRowId)
        .then(result => {
          this.connectResult = result;
          this.connecting = false;
          // Re-verify so the lights reflect what actually changed.
          this.loadStatus();
        })
        .catch((err) => {
          this.connectResult = {
            done: false,
            error: this.apiErrorDetail(
              err,
              "Unable to connect the transaction to INSIGHT right now. Try again shortly."
            )
          };
          this.connecting = false;
        });
    },

    // Codex-H2: honest per-family wording, keyed off family.verified - "verified in INSIGHT" / "live"
    // wording renders ONLY for 'live'; 'shallow'/'anchor' read as present-but-not-live-verified.
    verifiedLabel: function(verified) {
      if (verified === "live") {
        return "verified in INSIGHT";
      }
      if (verified === "anchor") {
        return "anchor present - not yet live-verified";
      }
      if (verified === "shallow") {
        return "id present - not yet live-verified";
      }
      if (verified === "failed") {
        return "could not verify right now";
      }
      return "";
    },

    dotClass: function(light) {
      if (light === "green") {
        return "light-dot-green";
      }
      if (light === "amber") {
        return "light-dot-amber";
      }
      return "light-dot-red";
    },

    stepLabel: function(step) {
      if (step === "client") {
        return "INSIGHT Client";
      }
      if (step === "broker") {
        return "Producing broker";
      }
      if (step === "policy") {
        return "Policy take-up";
      }
      return step;
    }
  }
};
</script>

<style lang="scss" scoped>
.connection-light {
  display: flex;
  align-items: center;
}

.light-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}

.light-dot-green {
  background-color: #1bc5bd;
}

.light-dot-amber {
  background-color: #ffa800;
}

.light-dot-red {
  background-color: #f64e60;
}
</style>
